import * as amplitude from '@amplitude/analytics-browser'
import Bugsnag from '@bugsnag/js'
import { useState, useEffect, useCallback, useRef } from 'react'
import { useMediaQuery } from 'react-responsive'
import { Routes, Route, useLocation, useNavigate } from 'react-router-dom'

import {
  fetchPosts,
  fetchSetting,
  postAction,
  postComment,
  fetchGridPreview,
  fetchPostSkedPreview,
  fetchComment,
} from 'api'
import type { AccountSelectorProps } from 'Components'
import type { ConversationTimelineItem } from 'Components/conversation'
import { getLastStatusChangeMessage } from 'Components/conversation/utils'
import type { PostOrGridView } from 'Components/PostPreviewSection'
import { ensureError } from 'utils/ensureError'
import getReviewKeyFromURL from 'utils/getReviewKeyFromURL'
import { resolvePostPreviewVariant } from 'utils/postPreviewVariant'

import type { ExternalReviewSettings } from './api/ExternalReviewSettings'
import type { UpcomingPosts } from './api/UpcomingPosts'
import App from './App'
import AppMobile from './AppMobile'
import type { StatusKey } from './normalizer'
import {
  normalizeSetting,
  normalizeAccounts,
  normalizePosts,
  normalizeStatusKey,
  normalizeSkedPreviewPost,
  normalizeMessages,
  normalizeRepliedMessage,
  normalizeGridPreview,
  isPostIsStory,
} from './normalizer'

const Homepage: React.FC = () => {
  const isMobile = useMediaQuery({ maxWidth: 906 })

  const [isLoadingPost, setIsLoadingPost] = useState<boolean>(true)
  const [isLoadingComments, setIsLoadingComments] = useState<boolean>(false)

  const [posts, setPosts] = useState<any[]>([])

  const [skedPreviewPost, setSkedPreviewPost] = useState<any>([])

  const [accounts, setAccounts] = useState<AccountSelectorProps['accounts']>([])

  // sked preview need accounts to be built
  const [accountsRaw, setAccountsRaw] = useState<any>([])

  const [setting, setSetting] = useState<ExternalReviewSettings>({
    logoUrl: '',
    company: '',
    companyName: '',
    accounts: [],
    isConversationsEnabled: false,
    isReadOnly: false,
    collaborators: [],
    isStatusChangesVisible: true,
    isExternalNotesVisible: true,
    isPostActivityVisible: true,
    username: '',
    linkName: '',
  })
  const [statusKey, setStatusKey] = useState<StatusKey>({
    pending: '',
    approved: '',
    requestChange: '',
  })
  const [postRowId, setPostRowId] = useState('')

  const location = useLocation()
  const navigate = useNavigate()

  const [postId, setPostId] = useState('')
  const [lastAction, setLastAction] = useState<string | null>(null)

  const reviewKey = getReviewKeyFromURL(window.location.href)
  const hasReviewKey = Boolean(reviewKey)

  const [postActionLoading, setPostActionLoading] = useState<boolean>(false)
  const [showApprovalAnimation, setShowApprovalAnimation] =
    useState<boolean>(false)

  // Guard to ensure initial post selection only happens once
  const hasInitializedRef = useRef(false)
  const [messages, setMessages] = useState<
    | {
        postId: string
        messages?: ConversationTimelineItem[]
      }[]
    | undefined
  >([])
  const [isRepliedPosts, setIsRepliedPosts] = useState<string[]>([])
  const [isSubmittingReply, setIsSubmittingReply] = useState<boolean>(false)

  const [isGridPreviewAvailable, setIsGridPreviewAvailable] =
    useState<boolean>(false)

  const [postOrGridView, setpostOrGridView] = useState<PostOrGridView>('post')

  const [gridData, setGridData] = useState<any>(undefined)
  const previewVariant = resolvePostPreviewVariant(setting)

  const [inactiveError, setInactiveError] = useState<string>('')
  const [actionError, setActionError] = useState<string | null>(null)

  useEffect(() => {
    if (lastAction) {
      setLastAction(null)
    }
  }, [postId, lastAction])

  useEffect(() => {
    if (!hasReviewKey) {
      setIsLoadingPost(false)
      return
    }

    const doWork = async (reviewKey: string) => {
      try {
        const setting = await fetchSetting(reviewKey)
        const normalizedSetting = normalizeSetting(setting)

        const posts = await fetchPosts(reviewKey)

        if (!posts) {
          throw new Error('Failed to fetch posts')
        }

        setSetting(normalizedSetting)

        setAccountsRaw(setting.accounts)
        setAccounts(normalizeAccounts(setting))

        setPosts(normalizePosts(posts, normalizedSetting.isReadOnly))
        setStatusKey(normalizeStatusKey(setting))

        setIsLoadingPost(false)

        if (setting.adminId && setting.company) {
          const groupName = setting.company
          const grpIdentify = new amplitude.Identify().set(
            'AdminID',
            setting.adminId
          )
          amplitude.setGroup('Org', groupName)
          amplitude.groupIdentify('Org', groupName, grpIdentify)
        }
        if (setting.plan) {
          const identifyContext = new amplitude.Identify()
          identifyContext.set('plan', setting.plan)
          amplitude.identify(identifyContext)
        }
      } catch (error: unknown) {
        Bugsnag.notify(ensureError(error))
        if (error instanceof Error && error.message) {
          setInactiveError(`Error loading: ${error.message}`)
          return false
        }
        setInactiveError('This review link is inactive')
        return false
      }
    }
    doWork(reviewKey).catch((error: unknown) => {
      Bugsnag.notify(ensureError(error))
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasReviewKey, reviewKey])

  const savePostRowId = (posts: UpcomingPosts[], postId: string) => {
    for (const { posts: _posts, id } of posts) {
      const found = _posts.some((post) => post.id === postId)

      if (found) {
        setPostRowId(id)
        break
      }
    }
  }

  const selectPostByPostId = (posts: UpcomingPosts[], postId: string) => {
    return posts.map((item) => ({
      ...item,
      posts: item.posts.map((post) =>
        post.id === postId
          ? {
              ...post,
              isSelected: true,
            }
          : post
      ),
    }))
  }

  const navigateToSelectedPost = (nextPostId: string) => {
    const nextPath = `/review/${reviewKey}/posts/${nextPostId}`

    if (location.pathname === nextPath) {
      return
    }

    navigate(nextPath, {
      replace: true,
    })
  }

  useEffect(() => {
    if (!isLoadingPost && !hasInitializedRef.current) {
      hasInitializedRef.current = true

      // user come from sked app link without postId
      let postId = location.pathname.includes('posts')
        ? location.pathname.split('/').pop()
        : undefined

      if (!postId || isNaN(+postId) === false) {
        //select first post if there is a pending post
        postId = posts
          .flatMap((item) => item.posts)
          .find((item) => item.status === 'pending')?.id

        if (postId) {
          const firstPostInFirstElement = document.querySelector(
            `[data-postid="${postId}"]`
          ) as HTMLDivElement
          firstPostInFirstElement?.click()
        } else {
          const firstPostInFirstElement = document.querySelector(
            `[data-postindex="1"]`
          ) as HTMLDivElement
          firstPostInFirstElement?.click()
        }
      } else {
        // user come from sked app link with postId when a post is selected
        const nextPosts = selectPostByPostId(posts, postId)

        setPosts(nextPosts)
        setPostId(postId)
        savePostRowId(nextPosts, postId)

        const firstPostInFirstRowIsSelected = nextPosts.map((item) =>
          item.posts.find((_, i) => i === 0)
        )[0]?.isSelected

        if (!firstPostInFirstRowIsSelected) {
          const toBeSelectedPostElem = document.querySelector(
            `[data-postid="${postId}"`
          ) as HTMLDivElement

          // delay because posts take time to render before scroll
          const postDelay = setTimeout(() => {
            toBeSelectedPostElem?.scrollIntoView({
              behavior: 'smooth',
            })
          }, 300)
          return () => clearTimeout(postDelay)
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingPost])

  const handleAccountChange = (selelectedOptions: string[]) => {
    const nextPosts = posts
      .map((item) => ({
        ...item,
        posts: item.posts.map((post: { accounts: { name: string }[] }) => ({
          ...post,
          isVisible: post.accounts.some((account: { name: string }) =>
            selelectedOptions.includes(account.name)
          ),
        })),
      }))
      .map((item) => ({
        ...item,
        isVisible: item.posts.some(
          (post: { isVisible: any }) => post.isVisible
        ),
      }))

    setPosts(nextPosts)
  }

  const handleSetPosts = (
    postRowId: string,
    postId: string,
    status: string
  ) => {
    const nextPosts = posts.map((item) =>
      item.id === postRowId
        ? {
            ...item,
            posts: item.posts.map((post: { id: string }) =>
              post.id === postId
                ? { ...post, status, isUndo: status !== 'pending' }
                : post
            ),
          }
        : item
    )

    // For non-approved states, refresh the preview for the selected post
    if (status !== 'approved') {
      fetchPostSkedPreviewAsync(reviewKey, postId)
    }

    setPosts(nextPosts)
  }

  const handleSetPostsMobile = (postId: string, status: string) => {
    const nextPosts = posts.map((item) => ({
      ...item,
      posts: item.posts.map((post: { id: string }) =>
        post.id === postId
          ? { ...post, status, isUndo: status !== 'pending' }
          : post
      ),
    }))

    setPosts(nextPosts)
  }

  // select post
  const handleCardClick = (postRowId: string, clickedPostId: string) => {
    // Skip if already viewing this post to prevent redundant history.replaceState() calls
    if (postId === clickedPostId) {
      return
    }

    const nextPosts = posts.map((item) => ({
      ...item,
      posts:
        item.id === postRowId
          ? item.posts.map((post: { id: string }) => ({
              ...post,
              isSelected: post.id === clickedPostId,
            }))
          : item.posts.map((post: any) => ({ ...post, isSelected: false })),
    }))
    setPosts(nextPosts)
    setPostId(clickedPostId)
    savePostRowId(nextPosts, clickedPostId)

    navigateToSelectedPost(clickedPostId)
  }

  const handleNavigatePost = (postIndex: number) => {
    //find postId by postIndex
    const postId = posts
      .flatMap((o) => o.posts)
      .find((post) => post.postIndex === postIndex)?.id

    if (postId) {
      if (postId === location.pathname.split('/').pop()) {
        return
      }

      // select post
      const nextPosts = posts.map((item) => ({
        ...item,
        posts: item.posts.map((post: { id: any }) => ({
          ...post,
          isSelected: post.id === postId,
        })),
      }))

      setPosts(nextPosts)
      setPostId(postId)
      savePostRowId(nextPosts, postId)

      navigateToSelectedPost(postId)

      const toBeSelectedPostElem = document.querySelector(
        `[data-postid="${postId}"`
      ) as HTMLDivElement

      toBeSelectedPostElem?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'nearest',
      })
    }
  }

  const fetchCommentAsync = async (reviewKey: string, postId: string) => {
    setIsLoadingComments(true)

    try {
      const messagesResponse = await fetchComment(reviewKey, postId)
      const nextMessages = [
        {
          postId,
          messages: messagesResponse
            ? normalizeMessages(messagesResponse) || []
            : [],
        },
      ]

      setMessages(nextMessages)
      return nextMessages
    } catch (error) {
      Bugsnag.notify(ensureError(error))
      // Return empty messages on failure so UI doesn't break
      return [{ postId, messages: [] }]
    } finally {
      setIsLoadingComments(false)
    }
  }

  const fetchPostSkedPreviewAsync = async (
    reviewKey: string,
    postId: string
  ) => {
    const skedPreviewResponse = await fetchPostSkedPreview(reviewKey, postId)

    if (!skedPreviewResponse) {
      setSkedPreviewPost(null)
      return
    }

    const normalizedSkedPreviewPost = normalizeSkedPreviewPost(
      skedPreviewResponse,
      accountsRaw
    )

    setSkedPreviewPost(normalizedSkedPreviewPost)

    if (
      skedPreviewResponse &&
      skedPreviewResponse.platform === 'IG' &&
      skedPreviewResponse.post_stage !== 'draft' &&
      !isPostIsStory(skedPreviewResponse)
    ) {
      const gridPreviewResponse = await fetchGridPreview(reviewKey, postId)

      if (gridPreviewResponse) {
        const normalizedGridPreviewPost = normalizeGridPreview(
          gridPreviewResponse,
          postId
        )
        setIsGridPreviewAvailable(true)
        setGridData(normalizedGridPreviewPost)
      }
    } else {
      setpostOrGridView('post')
      setIsGridPreviewAvailable(false)
    }
  }

  // postId changed
  useEffect(() => {
    if (posts.length && reviewKey && !isLoadingPost && postId && !isMobile) {
      if (posts.length) {
        // fetchCommentAsync handles its own errors internally
        fetchCommentAsync(reviewKey, postId)
        fetchPostSkedPreviewAsync(reviewKey, postId).catch((error: unknown) => {
          Bugsnag.notify(ensureError(error))
        })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [posts.length, isLoadingPost, reviewKey, postId, isMobile])

  const scrollToForm = () => {
    const textareaElem = document.querySelector(
      `[data-textarea-postid="${postId}"]`
    )

    // scroll to bottom 4%
    const toScrollPosition = window.innerHeight / 4
    textareaElem?.scrollIntoView(false)
    window.scrollBy(0, +toScrollPosition)
  }

  const scrollToPostInList = (postId: string) => {
    // Use setTimeout to ensure the DOM has updated after navigation
    setTimeout(() => {
      const postElement = document.querySelector(`[data-postid="${postId}"]`)
      const postListColumn = document.getElementById('post-list-column')

      if (postElement && postListColumn) {
        // Get the positions
        const postRect = postElement.getBoundingClientRect()
        const columnRect = postListColumn.getBoundingClientRect()

        // Calculate the scroll position to center the post in the viewport
        const scrollTop =
          postListColumn.scrollTop +
          postRect.top -
          columnRect.top -
          columnRect.height / 2 +
          postRect.height / 2

        // Smooth scroll to the post
        postListColumn.scrollTo({
          top: scrollTop,
          behavior: 'smooth',
        })
      }
    }, 100)
  }

  const navigateToNextPost = () => {
    const allPosts = posts.flatMap((item) => item.posts)
    const currentIndex = allPosts.findIndex((post) => post.id === postId)

    // Find the next pending post after the current one
    const nextPendingPost = allPosts
      .slice(currentIndex + 1)
      .find((post) => post.status === 'pending')

    if (nextPendingPost) {
      // Navigate to the next pending post after current
      const postRow = posts.find((item) =>
        item.posts.some((p: any) => p.id === nextPendingPost.id)
      )
      if (postRow) {
        handleCardClick(postRow.id, nextPendingPost.id)
        scrollToPostInList(nextPendingPost.id)
      }
    } else {
      // If no pending post after current, loop around to find the first pending post
      const firstPendingPost = allPosts.find(
        (post) => post.status === 'pending'
      )
      if (firstPendingPost) {
        const postRow = posts.find((item) =>
          item.posts.some((p: any) => p.id === firstPendingPost.id)
        )
        if (postRow) {
          handleCardClick(postRow.id, firstPendingPost.id)
          scrollToPostInList(firstPendingPost.id)
        }
      }
    }
  }

  const handleApprovalAnimationComplete = () => {
    setShowApprovalAnimation(false)
    setLastAction('approved')
    navigateToNextPost()
  }

  const handlePostAction = async (postId: string, status: string) => {
    setPostActionLoading(true)
    setActionError(null) // Clear any previous error

    try {
      if (status === 'approved') {
        await postAction(reviewKey, postId, statusKey['approved'])
        handleSetPosts(postRowId, postId, status)
        await fetchCommentAsync(reviewKey, postId)

        // Show approval animation
        setShowApprovalAnimation(true)
        setPostActionLoading(false)
        // Don't set lastAction yet, will be set after animation completes
        return
      } else if (status === 'requestChange') {
        await postAction(reviewKey, postId, statusKey['requestChange'])
        handleSetPosts(postRowId, postId, status)
        await fetchCommentAsync(reviewKey, postId)
        if (isMobile) {
          scrollToForm()
        }
      } //for undo
      else if (status === 'pending') {
        await postAction(reviewKey, postId, statusKey['pending'])
        setIsRepliedPosts(isRepliedPosts.filter((postId) => postId !== postId))

        // Hide approval animation if it's showing
        setShowApprovalAnimation(false)

        // undo is clicked when the user has not selected a post, let's call it specialUndo
        const postIdInURL = location.pathname.split('/')[4]
        const specialUndo = postIdInURL !== postId
        if (!specialUndo) {
          handleSetPosts(postRowId, postId, status)
          await fetchCommentAsync(reviewKey, postId)
        } else {
          const nextPosts = posts.map((item) => ({
            ...item,
            posts: item.posts.map((post: { id: string }) =>
              post.id === postId
                ? { ...post, status, isSelected: true, isUndo: false }
                : { ...post, isSelected: false }
            ),
          }))

          setPosts(nextPosts)
          setTimeout(() => {
            const element = document.querySelector(
              `[data-postid="${postId}"`
            ) as HTMLDivElement
            if (element) {
              element.click()
            }
          }, 300)
        }
      }

      setLastAction(status)

      if (isMobile) {
        handleSetPostsMobile(postId, status)
      }
    } catch (error) {
      Bugsnag.notify(ensureError(error))
      setActionError('Action failed. Please try again.')
      // Clear error after 5 seconds
      setTimeout(() => setActionError(null), 5000)
    } finally {
      setPostActionLoading(false)
    }
  }

  const handleSubmitReply = async (
    text: string,
    postId: string,
    commentId: string
  ) => {
    setIsSubmittingReply(true)

    // Track analytics for reply submission
    amplitude.track('ext-rejection-message-sent')

    try {
      const messagesResponse = await postComment(
        reviewKey,
        postId,
        text,
        commentId
      )

      if (!messagesResponse) {
        Bugsnag.notify(
          new Error(
            'Post comment failed so not continuing handling submit reply'
          ),
          function (event) {
            event.addMetadata('default', {
              postId,
              commentId,
              messagesResponse,
            })
          }
        )
        return
      }
      const message = normalizeRepliedMessage(messagesResponse)
      const nextMessage = messages?.map((item) =>
        item.postId === postId
          ? {
              ...item,
              messages: [...(item.messages || []), message],
            }
          : item
      )
      setMessages(nextMessage)
      setIsRepliedPosts([...isRepliedPosts, postId])
      setLastAction('submitChangeDetail')
    } catch (error) {
      Bugsnag.notify(ensureError(error))
    } finally {
      setIsSubmittingReply(false)
    }
  }

  // sked-preview related states
  const totalPostLength = posts.flatMap((item) => item.posts).length
  const currentPostIndex =
    posts.flatMap((item) => item.posts).find((post) => post.isSelected)
      ?.postIndex ?? 0

  // conversations related stats
  const currentPostStatus =
    posts
      .find((item) => item.id === postRowId)
      ?.posts.find((post: { id: string }) => post.id === postId)?.status ??
    'pending'

  const postMessages =
    currentPostStatus === 'requestChange'
      ? messages?.find((item: { postId: string }) => item.postId === postId)
      : undefined

  const lastStatusChangeMessage = postMessages?.messages
    ? getLastStatusChangeMessage(postMessages.messages)
    : null

  const lastStatusChangeComment = lastStatusChangeMessage?.comment

  const hasPostedComment = Boolean(
    lastStatusChangeMessage?.isMessageOwner &&
    lastStatusChangeComment &&
    lastStatusChangeComment.trim().length > 0
  )

  const lastMessageId = lastStatusChangeMessage?.id

  const updatePostState = useCallback(
    (_reviewKey: string, postId: string, postStatus: string) => {
      if (!postActionLoading) {
        // Track analytics inside the guard so they only fire when action proceeds
        if (postStatus === 'pending') {
          amplitude.track('ext-undo')
        } else if (postStatus === 'approved') {
          amplitude.track('ext-approved-click')
        } else if (postStatus === 'requestChange') {
          amplitude.track('ext-rejection-click')
        }

        handlePostAction(postId, postStatus).catch((error: unknown) => {
          Bugsnag.notify(ensureError(error))
        })
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [postActionLoading, handlePostAction, isMobile]
  )

  if (inactiveError) {
    return (
      <p
        style={{
          margin: '10px',
          fontSize: '14px',
        }}
      >
        {inactiveError}
      </p>
    )
  }

  return (
    <Routes>
      <Route
        path="/"
        element={
          <div
            style={{
              margin: '10px',
              fontSize: '14px',
            }}
          >
            Invalid link, the correct link pattern should be /review/:review-key
          </div>
        }
      />
      <Route
        path={`review/*`}
        element={
          isMobile ? (
            <div>
              <AppMobile
                isLoading={isLoadingPost}
                setting={{
                  logoUrl: setting.logoUrl,
                  companyName: setting.companyName,
                }}
                accountSelectorProps={{
                  accounts,
                  onChange: handleAccountChange,
                }}
                upcomingPosts={posts}
                accountsRaw={accountsRaw}
                updatePostState={updatePostState}
                conversationProps={{
                  isLoading: isLoadingComments,
                  messages,
                  isRepliedPosts,
                  postId,
                  reviewKey, // mobile
                  fetchComment: fetchCommentAsync, // mobile
                  isConversationsEnabled: setting.isConversationsEnabled,
                  collaborators: setting.collaborators || [],
                  isReadOnly: setting.isReadOnly,
                  isStatusChangesVisible: setting.isStatusChangesVisible,
                  isExternalNotesVisible: setting.isExternalNotesVisible,
                  isPostActivityVisible: setting.isPostActivityVisible,
                  username: setting.username,
                  isReplied:
                    hasPostedComment || isRepliedPosts.includes(postId),
                }}
                postActionProps={{
                  isLoading: postActionLoading,
                  postStatus: currentPostStatus,
                  postId,
                  reviewKey,
                  updatePostState,
                  // Reply functionality props
                  canReply: currentPostStatus === 'requestChange',
                  isReplied:
                    hasPostedComment || isRepliedPosts.includes(postId),
                  isSubmitting: isSubmittingReply,
                  isConversationsEnabled: setting.isConversationsEnabled,
                  onReplySubmit: handleSubmitReply,
                  lastMessageId: lastMessageId,
                  collaborators: setting.collaborators || [],
                  isMobile: true,
                  // Approval animation props
                  showApprovalAnimation,
                  onApprovalAnimationComplete: handleApprovalAnimationComplete,
                  // Error state
                  actionError,
                }}
                reviewKey={reviewKey}
                postId={postId}
                isReadOnly={setting.isReadOnly}
                previewVariant={previewVariant}
              />
            </div>
          ) : (
            <App
              lastAction={lastAction}
              isLoading={isLoadingPost}
              setting={{
                logoUrl: setting.logoUrl,
                companyName: setting.companyName,
              }}
              accountSelectorProps={{
                accounts,
                onChange: handleAccountChange,
              }}
              upcomingPosts={posts}
              handleCardClick={handleCardClick}
              navigatePost={handleNavigatePost}
              postPreviewSectionProps={{
                currentPostIndex,
                totalPostLength,
                navigatePost: handleNavigatePost,
                skedPreviewPost,
                postOrGridView,
                setpostOrGridView,
                isGridPreviewAvailable,
                gridData,
                previewVariant,
              }}
              conversationProps={{
                status: currentPostStatus,
                isLoading: isLoadingComments,
                messages,
                isReplied: hasPostedComment || isRepliedPosts.includes(postId),
                postId,
                isConversationsEnabled: setting.isConversationsEnabled,
                collaborators: setting.collaborators || [],
                isReadOnly: setting.isReadOnly,
                isStatusChangesVisible: setting.isStatusChangesVisible,
                isExternalNotesVisible: setting.isExternalNotesVisible,
                isPostActivityVisible: setting.isPostActivityVisible,
                username: setting.username,
              }}
              updatePostState={updatePostState}
              postActionProps={{
                isLoading: postActionLoading,
                postStatus: currentPostStatus,
                postId,
                reviewKey,
                updatePostState,
                // Reply functionality props
                canReply: currentPostStatus === 'requestChange',
                isReplied: hasPostedComment || isRepliedPosts.includes(postId),
                isSubmitting: isSubmittingReply,
                isConversationsEnabled: setting.isConversationsEnabled,
                onReplySubmit: handleSubmitReply,
                lastMessageId: lastMessageId,
                collaborators: setting.collaborators || [],
                isMobile: false,
                // Approval animation props
                showApprovalAnimation,
                onApprovalAnimationComplete: handleApprovalAnimationComplete,
                // Error state
                actionError,
              }}
              isReadOnly={setting.isReadOnly}
              previewVariant={previewVariant}
            />
          )
        }
      />
    </Routes>
  )
}

export default Homepage
